"use client";

import React, { createRef, type ComponentProps } from "react";
import { type DeepReadonly } from "@/utils/tsmagic";
import { Player, type PlayerRef } from "@remotion/player";
import { proxy, useSnapshot } from "valtio";

import { type WrapVideo } from "@matan/database";

import { type ReadOnlyCardObjects } from "./core/derivedSchema";
import RootComposition from "./RootComposition";

type Props = Partial<ComponentProps<typeof Player>> & {
  readonly wrapVideo: DeepReadonly<WrapVideo>;
  readonly cardObjects: ReadOnlyCardObjects;
  withEndplate?: boolean;
  clickToPlay?: boolean;
  /**
   * Enable IP protection features:
   * - Disables download button in video controls
   * - Disables remote playback and picture-in-picture
   * - Prevents right-click context menu on video
   * Use for consumer-facing modes (view, preview, consumer editor)
   */
  disableDownload?: boolean;
};

export const RemotionPlayerRef = proxy(createRef<PlayerRef>());
export const usePlayerRef = () => useSnapshot(RemotionPlayerRef);

const RemotionPlayer = ({
  wrapVideo,
  cardObjects,
  withEndplate,
  disableDownload = false,
  ...props
}: Props) => {
  const durationInFrames = withEndplate
    ? wrapVideo.durationInFrames + 60
    : wrapVideo.durationInFrames;

  const inputProps = React.useMemo(() => {
    return {
      cardId: undefined,
      data: {
        cardObjects,
        wrapVideo,
      },
      withEndplate,
    };
  }, [cardObjects, withEndplate, wrapVideo]);

  // IP Protection: Disable download and restrict video access
  const playerProps = disableDownload ? {
    ...props,
    // Disable download button in HTML5 video controls
    controlsList: 'nodownload nofullscreen noremoteplayback',
    // Disable picture-in-picture mode
    disablePictureInPicture: true,
    // Disable remote playback (Chromecast, AirPlay, etc.)
    disableRemotePlayback: true,
  } : props;

  return (
    <div
      className={disableDownload ? 'select-none' : undefined}
      onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
    >
      <Player
        {...playerProps}
        ref={RemotionPlayerRef}
        inputProps={inputProps}
        component={RootComposition}
        durationInFrames={durationInFrames}
        compositionWidth={wrapVideo.resolutionX}
        compositionHeight={wrapVideo.resolutionY}
        fps={wrapVideo.fps}
        className="max-h-full rounded-lg bg-white shadow-sm lg:grow"
        moveToBeginningWhenEnded={false}
      />
    </div>
  );
};

export const RemotionPlayerPreview = ({
  wrapVideo,
  cardObjects,
  style,
  autoPlay,
  controls,
  loop,
  disableDownload = false,
}: Props) => {
  const inputProps = React.useMemo(() => {
    return {
      cardId: undefined,
      data: {
        cardObjects,
        wrapVideo,
      },
    };
  }, [cardObjects, wrapVideo]);

  const protectionProps = disableDownload
    ? {
        controlsList: "nodownload nofullscreen noremoteplayback" as const,
        disablePictureInPicture: true,
        disableRemotePlayback: true,
      }
    : {};

  return (
    <div
      className={disableDownload ? "h-full w-full select-none" : "h-full w-full"}
      onContextMenu={
        disableDownload ? (e) => e.preventDefault() : undefined
      }
    >
      <Player
        inputProps={inputProps}
        initiallyMuted
        component={RootComposition}
        durationInFrames={wrapVideo.durationInFrames}
        compositionWidth={wrapVideo.resolutionX}
        compositionHeight={wrapVideo.resolutionY}
        fps={wrapVideo.fps}
        className="max-h-full rounded-lg bg-white shadow-sm lg:grow"
        style={style}
        loop={loop}
        moveToBeginningWhenEnded={false}
        autoPlay={autoPlay}
        controls={controls}
        {...protectionProps}
      />
    </div>
  );
};

export default RemotionPlayer;
